import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { formatDate } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DATE_FORMATS } from '@app/core/models/date-adapter';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { TranslateService } from '@app/core/services/translate.service';
import { UserStore } from '@app/core/services/user.store';
import { UtilityService } from '@app/core/services/utility.service';
import { Day } from '@progress/kendo-date-math';
import { IntlService } from '@progress/kendo-angular-intl';
import { DateChangeEvent, DateRange, DragEndEvent, EditableSettings, EventClickEvent, EventStyleArgs, Group, RemoveEvent, ResizeEndEvent, Resource, SaveEvent, SchedulerEvent, SlotClickEvent } from '@progress/kendo-angular-scheduler';
import { SlotRange } from '@progress/kendo-angular-scheduler/types/slot-selection';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { KendoBaseComponent } from '../kendo-base/kendo-base.component';

import '@progress/kendo-angular-intl/locales/zh/all';
import '@progress/kendo-angular-intl/locales/pl/all';
import '@progress/kendo-angular-intl/locales/it/all';
import '@progress/kendo-angular-intl/locales/fr/all';
import '@progress/kendo-angular-intl/locales/lt/all';
import '@progress/kendo-angular-intl/locales/fi/all';
import '@progress/kendo-angular-intl/locales/es/all';
import '@progress/kendo-angular-intl/locales/de/all';
import '@progress/kendo-angular-intl/locales/da/all';
import '@progress/kendo-angular-intl/locales/se/all';
import '@progress/kendo-angular-intl/locales/no/all';

@Component({
    selector: 'tt-scheduler',
    templateUrl: './scheduler.component.html',
    styleUrls: ['./scheduler.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class SchedulerComponent<TItem extends SchedulerDataItem = SchedulerDataItem> extends KendoBaseComponent implements OnChanges, AfterViewInit, OnDestroy {
    /**
     * Id used for persisting scheduler state using localStorage and RememberService.
     */
    @Input()
    public ttRememberId = '';

    /**
     * Id used for persisting the selected date as a raw local yyyy-MM-dd string.
     */
    @Input()
    public ttRememberDateId = '';

    /**
     * Milliseconds between persisted scheduler state changes.
     *
     * @default 500
     */
    @Input()
    public set ttRememberDebounceTime(value: NumberInput) {
        this._rememberDebounceTime = coerceNumberProperty(value, 500);
    }
    public get ttRememberDebounceTime(): number {
        return this._rememberDebounceTime;
    }
    private _rememberDebounceTime = 500;

    /**
     * When true, state is saved but not restored on initialization.
     *
     * @default false
     */
    @Input()
    public set ttOnlyRemember(value: BooleanInput) {
        this._onlyRemember = coerceBooleanProperty(value);
    }
    public get ttOnlyRemember(): boolean {
        return this._onlyRemember;
    }
    private _onlyRemember = false;

    /**
     * The scheduler events. Supports a flat list of legacy-like records or a grouped object with `items` and `groups`.
     */
    @Input()
    public ttData: SchedulerDataInput<TItem> = [];

    /**
     * Optional scheduler resource list. Legacy records can still use `portal_user_keyno` and `portal_user_name`.
     */
    @Input()
    public ttResources: SchedulerDataItem[] = [];

    /**
     * Backwards-compatible alias for `ttResources`.
     *
     * @deprecated Use `ttResources`.
     */
    @Input()
    public ttOwners: SchedulerDataItem[] = [];

    /**
     * Optional scheduler setup. This keeps common legacy setup fields available without calling legacy callbacks.
     */
    @Input()
    public ttSetup: SchedulerSetup = {};

    /**
     * Controls which standard Kendo views are rendered.
     */
    @Input()
    public ttMode: SchedulerMode = 'default';

    /**
     * The selected date. If omitted, `ttSetup.date_fom` is used when available.
     */
    @Input()
    public ttSelectedDate?: string;

    /**
     * Milliseconds to debounce selected date change output.
     *
     * @default 0
     */
    @Input()
    public set ttSelectedDateDebounceTime(value: NumberInput) {
        this._selectedDateDebounceTime = coerceNumberProperty(value, 0);
        this.configureSelectedDateSubscription();
    }
    public get ttSelectedDateDebounceTime(): number {
        return this._selectedDateDebounceTime;
    }
    private _selectedDateDebounceTime = 0;

    /**
     * The selected view name. If omitted, `ttSetup.selectedview` is used when available.
     */
    @Input()
    public ttSelectedView?: SchedulerViewName | string;

    /**
     * Controls whether events can be edited by dragging/resizing.
     *
     * @default true
     */
    @Input()
    public set ttEditable(value: BooleanInput) {
        this._editable = coerceBooleanProperty(value);
    }
    public get ttEditable(): boolean {
        return this._editable;
    }
    private _editable = false;

    /**
     * Controls whether Kendo's remove x is shown on events.
     *
     * @default true
     */
    @Input()
    public set ttShowRemoveButton(value: BooleanInput) {
        this._showRemoveButton = coerceBooleanProperty(value);
    }
    public get ttShowRemoveButton(): boolean {
        return this._showRemoveButton;
    }
    private _showRemoveButton = true;

    /**
     * Enables selecting scheduler slots and slot ranges.
     *
     * @default false
     */
    @Input()
    public set ttSlotSelection(value: BooleanInput) {
        this._slotSelection = coerceBooleanProperty(value);
    }
    public get ttSlotSelection(): boolean {
        return this._slotSelection;
    }
    private _slotSelection = false;

    @Output()
    public ttSelectedDateChange = new EventEmitter<string>();

    @Output()
    public ttSelectedViewChange = new EventEmitter<SchedulerViewName>();

    @Output()
    public ttEventChange = new EventEmitter<SchedulerItemChange<TItem>>();

    @Output()
    public ttRemove = new EventEmitter<TItem>();

    @Output()
    public ttDateRangeChange = new EventEmitter<SchedulerDateRangeChange>();

    @Output()
    public ttEventClick = new EventEmitter<SchedulerEventClick<TItem>>();

    @Output()
    public ttSlotSelected = new EventEmitter<SchedulerSlotSelected>();

    @Output()
    public ttSlotClick = new EventEmitter<SchedulerSlotClick>();

    public events: SchedulerDisplayEvent<TItem>[] = [];

    public resources: Resource[] = [];

    public group: Group = {
        resources: [],
        orientation: 'vertical',
    };

    public slotSelection?: SlotRange;

    public selectedDate = new Date();

    public selectedViewIndex = 0;

    public viewNames: SchedulerViewName[] = this.getViewNamesForMode(this.ttMode);

    public editableSettings: EditableSettings = {
        remove: true,
        drag: true,
        resize: true,
        add: false,
        edit: false,
    };

    public eventHeight = 50;

    public columnWidth = 100;

    public showWorkHours = true;

    public allDaySlot = false;

    public weekStart: Day = 1 as Day;

    public startTime = '00:00';

    public endTime = '00:00';

    public workDayStart = '08:00';

    public workDayEnd = '17:00';

    public slotDuration = 60;

    public slotDivisions = 2;

    public scrollTime: string | Date = '08:00';

    public schedulerLocale = 'en';

    public schedulerDateFormat = DATE_FORMATS.display.dateInput.format;

    public schedulerSingleDateFormat = `{0:${this.schedulerDateFormat}}`;

    public schedulerRangeDateFormat = `{0:${this.schedulerDateFormat}} - {1:${this.schedulerDateFormat}}`;

    public schedulerTimeFormat = 'HH:mm';

    public schedulerMessages: SchedulerMessages = { ...SCHEDULER_MESSAGE_FALLBACKS };

    public eventStyles = (args: EventStyleArgs): { [key: string]: string | number } => {
        const schedulerEvent = args.event as SchedulerDisplayEvent<TItem>;
        const data = this.getEventDataItem(schedulerEvent);
        const style: { [key: string]: string | number } = {};
        const eventColor = this.getEventStyleColor(schedulerEvent, data);
        const eventTextColor = this.getEventStyleTextColor(schedulerEvent, data) ?? this.getContrastTextColor(eventColor);

        if (eventColor !== undefined) {
            style['backgroundColor'] = eventColor;
            style['borderColor'] = eventColor;
        }

        if (eventTextColor !== undefined) {
            style['color'] = eventTextColor;
        }

        return style;
    };

    public schedulerReady = false;

    private readonly schedulerStorageKey = 'tt-scheduler';

    private readonly schedulerDateStorageKey = 'tt-scheduler-date';

    private rememberSubject = new Subject<void>();

    private rememberSubscription?: Subscription;

    private rememberDateSubject = new Subject<string>();

    private rememberDateSubscription?: Subscription;

    private selectedDateSubject = new Subject<string>();

    private selectedDateSubscription?: Subscription;

    private localisationSubscriptions = new Subscription();

    private schedulerMessageRequestId = 0;

    private schedulerReadyTimeout?: ReturnType<typeof setTimeout>;

    private selectedDateStateApplied = false;

    private selectedViewStateApplied = false;

    private showWorkHoursStateApplied = false;

    private lastRememberLoadId = '';

    private lastRememberDateLoadId = '';

    constructor(
        private remember: RememberService,
        private stateService: StateService,
        private userStore: UserStore,
        private translate: TranslateService,
        private utility: UtilityService,
        private intl: IntlService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.initialiseLocalisation();
        this.loadRememberedState();
        this.loadRememberedDate();
    }

    ngAfterViewInit(): void {
        this.schedulerReadyTimeout = setTimeout(() => {
            this.schedulerReady = true;
            this.cdr.detectChanges();
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        clearTimeout(this.schedulerReadyTimeout);
        this.rememberSubscription?.unsubscribe();
        this.rememberDateSubscription?.unsubscribe();
        this.selectedDateSubscription?.unsubscribe();
        this.localisationSubscriptions.unsubscribe();
        this.rememberSubject.complete();
        this.rememberDateSubject.complete();
        this.selectedDateSubject.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttRememberDebounceTime']) {
            this.ttRememberDebounceTime = changes['ttRememberDebounceTime'].currentValue;
        }

        if (changes['ttOnlyRemember']) {
            this.ttOnlyRemember = changes['ttOnlyRemember'].currentValue;
        }

        if (changes['ttSelectedDateDebounceTime']) {
            this.ttSelectedDateDebounceTime = changes['ttSelectedDateDebounceTime'].currentValue;
        }

        if (changes['ttEditable']) {
            this.ttEditable = changes['ttEditable'].currentValue;
        }

        if (changes['ttShowRemoveButton']) {
            this.ttShowRemoveButton = changes['ttShowRemoveButton'].currentValue;
        }

        if (changes['ttSlotSelection']) {
            this.ttSlotSelection = changes['ttSlotSelection'].currentValue;
        }

        if (changes['ttRememberId'] || changes['ttRememberDateId'] || changes['ttRememberDebounceTime']) {
            this.configureRememberSubscription();
        }

        this.viewNames = this.getViewNamesForMode(this.ttMode);
        this.applySetup();
        this.syncSelectedDate(changes);
        this.syncSelectedView(changes);
        this.rebuildSchedulerData();

        if ((changes['ttRememberId'] || changes['ttOnlyRemember']) && !changes['ttRememberId']?.firstChange && !changes['ttOnlyRemember']?.firstChange) {
            this.loadRememberedState();
        }

        if ((changes['ttRememberDateId'] || changes['ttOnlyRemember']) && !changes['ttRememberDateId']?.firstChange && !changes['ttOnlyRemember']?.firstChange) {
            this.loadRememberedDate();
        }
    }

    public hasView(viewName: SchedulerViewName): boolean {
        return this.viewNames.includes(viewName);
    }

    public onSelectedViewIndexChange(index: number): void {
        if (index === this.selectedViewIndex) {
            return;
        }

        this.selectedViewIndex = index;
        this.selectedViewStateApplied = true;
        const view = this.selectedViewName;

        this.ttSelectedViewChange.emit(view);
        this.emitDateRangeChange(this.buildDateRange(this.selectedDate, view));
        this.rememberStateChange();
    }

    public onDateChange(event: DateChangeEvent): void {
        if (this.isSameDateTime(event.selectedDate, this.selectedDate)) {
            return;
        }

        this.selectedDate = new Date(event.selectedDate);
        this.selectedDateStateApplied = true;
        this.emitSelectedDateChange(getLocalDateAsString(event.selectedDate));
        this.emitDateRangeChange(event.dateRange);
        this.rememberStateChange();
        this.rememberDateChange();
    }

    public onSchedulerClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;

        if (!target?.closest?.('.k-scheduler-fullday')) {
            return;
        }

        this.showWorkHours = !this.showWorkHours;
        this.showWorkHoursStateApplied = true;
        this.rememberStateChange();
    }

    public onEventClick(event: EventClickEvent): void {
        const schedulerEvent = event.event as SchedulerDisplayEvent<TItem>;
        const item = this.getEventDataItem(schedulerEvent);

        this.ttEventClick.emit({
            item,
            schedulerEvent,
            originalEvent: event.originalEvent,
        });

        this.navigateEventItem(item, event.originalEvent);
    }

    public onSlotClick(event: SlotClickEvent): void {
        this.ttSlotClick.emit(this.buildSlotSelectedEvent(event) as SchedulerSlotClick);
    }

    public onSlotSelectionChange(selection: SlotRange): void {
        if (!this.ttSlotSelection) {
            return;
        }

        this.slotSelection = selection;
        this.ttSlotSelected.emit(this.buildSlotSelectedEvent(selection));
    }

    public onDragEnd(event: DragEndEvent): void {
        this.emitItemChange('drag', event.dataItem as SchedulerDisplayEvent<TItem>, event.start, event.end, event.event as SchedulerDisplayEvent<TItem>);
    }

    public onResizeEnd(event: ResizeEndEvent): void {
        this.emitItemChange('resize', event.dataItem as SchedulerDisplayEvent<TItem>, event.start, event.end, event.event as SchedulerDisplayEvent<TItem>);
    }

    public onSave(event: SaveEvent): void {
        const formValue = event.formGroup?.value ?? {};
        const start = this.toDate(formValue['start'] ?? event.dataItem?.start ?? event.dataItem?.start_dt);
        const end = this.toDate(formValue['end'] ?? event.dataItem?.end ?? event.dataItem?.end_dt);

        if (start && end) {
            this.emitItemChange('save', event.dataItem as SchedulerDisplayEvent<TItem>, start, end);
        }
    }

    public onRemove(event: RemoveEvent): void {
        const item = this.getEventDataItem(event.dataItem as SchedulerDisplayEvent<TItem>);

        if (this.isReadOnlyItem(item) || this.isReadOnlyItem(event.event as SchedulerDisplayEvent<TItem>)) {
            event.preventDefault();
            return;
        }

        this.ttRemove.emit(item);
    }

    public getEventDescription(event: SchedulerDisplayEvent<TItem>): string {
        const data = this.getEventDataItem(event);

        return `${data?.['description'] ?? event.description ?? ''}`;
    }

    public getEventDescriptionHtml(event: SchedulerDisplayEvent<TItem>): SafeHtml | undefined {
        const description = this.getEventDescription(event);

        if (description === '') {
            return undefined;
        }

        if (event['__ttDescriptionText'] !== description) {
            event['__ttDescriptionText'] = description;
            event['__ttDescriptionHtml'] = this.sanitizer.bypassSecurityTrustHtml(description);
        }

        return event['__ttDescriptionHtml'] as SafeHtml;
    }

    public getEventSubText(event: SchedulerDisplayEvent<TItem>): string[] {
        const data = this.getEventDataItem(event);

        return [data?.['item_name_sub1'], data?.['item_name_sub2']].filter((text) => typeof text === 'string' && text > '') as string[];
    }

    public formatSchedulerDate(value: unknown, format = this.schedulerDateFormat): string {
        const date = this.toDate(value);

        if (!date) {
            return '';
        }

        try {
            return formatDate(date, format, this.schedulerLocale);
        } catch (error) {
            return formatDate(date, format, 'en');
        }
    }

    public formatSchedulerHeaderDate(value: unknown): string {
        return this.formatSchedulerDate(value, `EEE ${this.schedulerDateFormat}`);
    }

    public formatSchedulerTime(value: unknown): string {
        return this.formatSchedulerDate(value, this.schedulerTimeFormat);
    }

    public formatSchedulerTimeRange(start: unknown, end: unknown): string {
        const startText = this.formatSchedulerTime(start);
        const endText = this.formatSchedulerTime(end);

        return startText && endText ? `${startText} - ${endText}` : startText || endText;
    }

    public formatSchedulerAgendaTime(start: unknown, end: unknown, isAllDay: boolean): string {
        return isAllDay ? this.schedulerMessages.allDay : this.formatSchedulerTimeRange(start, end);
    }

    public get selectedViewName(): SchedulerViewName {
        return this.viewNames[this.selectedViewIndex] ?? this.getDefaultViewForMode(this.ttMode);
    }

    private initialiseLocalisation(): void {
        this.updateSchedulerLocale();
        this.updateSchedulerDateFormats();
        this.translateSchedulerMessages();

        this.localisationSubscriptions.add(
            this.userStore.languageIdChanged.subscribe({
                next: () => {
                    if (this.updateSchedulerLocale()) {
                        this.translateSchedulerMessages();
                    }
                },
            }),
        );

        this.localisationSubscriptions.add(
            this.userStore.dateFormatChanged.subscribe({
                next: () => this.updateSchedulerDateFormats(),
            }),
        );
    }

    private updateSchedulerLocale(): boolean {
        const locale = this.normalizeSchedulerLocale(DATE_FORMATS.display.dateInput.language || this.userStore.languageId || navigator.language || navigator.languages?.[0] || 'en');

        if (locale === this.schedulerLocale) {
            return false;
        }

        this.schedulerLocale = locale;
        (this.intl as SchedulerIntlService).localeId = locale;
        this.cdr.markForCheck();

        return true;
    }

    private updateSchedulerDateFormats(): void {
        this.schedulerDateFormat = DATE_FORMATS.display.dateInput.format || 'dd/MM/yyyy';
        this.schedulerSingleDateFormat = `{0:${this.schedulerDateFormat}}`;
        this.schedulerRangeDateFormat = `{0:${this.schedulerDateFormat}} - {1:${this.schedulerDateFormat}}`;
        this.cdr.markForCheck();
    }

    private async translateSchedulerMessages(): Promise<void> {
        const requestId = ++this.schedulerMessageRequestId;
        const wordIds = Object.values(SCHEDULER_MESSAGE_WORD_IDS);

        try {
            const translations = await this.translate.translateBatch(wordIds);

            if (requestId !== this.schedulerMessageRequestId) {
                return;
            }

            this.schedulerMessages = Object.keys(SCHEDULER_MESSAGE_WORD_IDS).reduce(
                (messages, key) => {
                    const messageKey = key as keyof SchedulerMessages;
                    const wordId = SCHEDULER_MESSAGE_WORD_IDS[messageKey];
                    const translation = translations[wordId];

                    messages[messageKey] = translation && translation > '' ? translation : SCHEDULER_MESSAGE_FALLBACKS[messageKey];

                    return messages;
                },
                { ...SCHEDULER_MESSAGE_FALLBACKS },
            );

            this.cdr.markForCheck();
        } catch (error) {
            this.schedulerMessages = { ...SCHEDULER_MESSAGE_FALLBACKS };
        }
    }

    private normalizeSchedulerLocale(value: string): SchedulerLocale {
        const normalized = value.trim().replace('_', '-').toLowerCase();
        const language = normalized.split('-')[0] || 'en';

        switch (language) {
            case 'gb':
                return 'en';
            case 'dk':
                return 'da';
            case 'li':
                return 'lt';
            case 'cn':
                return 'zh';
            case 'nb':
            case 'nn':
            case 'sy':
                return 'no';
            default:
                return SCHEDULER_LOCALES.has(language as SchedulerLocale) ? (language as SchedulerLocale) : 'en';
        }
    }

    private applySetup(): void {
        const editableSettings = this.getEditableSettings(this.ttSetup?.editable);

        this.editableSettings = {
            ...editableSettings,
            drag: editableSettings.drag !== false && this.ttEditable,
            resize: editableSettings.resize !== false && this.ttEditable,
            edit: editableSettings.edit !== false && this.ttEditable,
            remove: editableSettings.remove !== false && this.ttShowRemoveButton,
        };
        this.eventHeight = this.toNumber(this.ttSetup?.eventHeight, 50);
        this.columnWidth = this.toNumber(this.ttSetup?.columnWidth, 100);

        if (!this.showWorkHoursStateApplied) {
            this.showWorkHours = this.toBoolean(this.ttSetup?.showWorkHours, true);
        }

        this.allDaySlot = this.toBoolean(this.ttSetup?.allDaySlot, false);
        this.weekStart = this.toNumber(this.ttSetup?.weekStart, 1) as Day;
        this.startTime = this.ttSetup?.startTime ?? '00:00';
        this.endTime = this.ttSetup?.endTime ?? '00:00';
        this.workDayStart = this.ttSetup?.workDayStart ?? '08:00';
        this.workDayEnd = this.ttSetup?.workDayEnd ?? '17:00';
        this.slotDuration = this.toNumber(this.ttSetup?.slotDuration, 60);
        this.slotDivisions = this.toNumber(this.ttSetup?.slotDivisions, 2);
        this.scrollTime = this.ttSetup?.scrollTime ?? '08:00';
    }

    private syncSelectedDate(changes: SimpleChanges): void {
        if (!changes['ttSelectedDate'] && this.selectedDateStateApplied) {
            return;
        }

        const selectedDate = this.toDate(this.ttSelectedDate) ?? this.toDate(this.ttSetup?.date_fom);

        if (selectedDate) {
            this.selectedDate = selectedDate;
        }
    }

    private syncSelectedView(changes: SimpleChanges): void {
        if (!changes['ttMode'] && !changes['ttSelectedView'] && this.selectedViewStateApplied) {
            return;
        }

        const requestedView = this.normalizeViewName(this.ttSelectedView ?? this.ttSetup?.selectedview);
        const selectedView = requestedView && this.viewNames.includes(requestedView) ? requestedView : this.getDefaultViewForMode(this.ttMode);
        const index = this.viewNames.indexOf(selectedView);

        this.selectedViewIndex = index > -1 ? index : 0;
    }

    private configureRememberSubscription(): void {
        this.rememberSubscription?.unsubscribe();
        this.rememberDateSubscription?.unsubscribe();
        delete this.rememberSubscription;
        delete this.rememberDateSubscription;

        if (this.ttRememberId) {
            this.rememberSubscription = this.rememberSubject.pipe(debounceTime(this.ttRememberDebounceTime)).subscribe({ next: () => this.rememberState() });
        }

        if (this.ttRememberDateId) {
            this.rememberDateSubscription = this.rememberDateSubject.pipe(debounceTime(this.ttRememberDebounceTime)).subscribe({ next: (value) => this.rememberDate(value) });
        }
    }

    private configureSelectedDateSubscription(): void {
        this.selectedDateSubscription?.unsubscribe();
        delete this.selectedDateSubscription;

        if (this.ttSelectedDateDebounceTime > 0) {
            this.selectedDateSubscription = this.selectedDateSubject.pipe(debounceTime(this.ttSelectedDateDebounceTime)).subscribe({ next: (value) => this.ttSelectedDateChange.emit(value) });
        }
    }

    private emitSelectedDateChange(value: string): void {
        this.ttSelectedDate = value;

        if (this.ttSelectedDateDebounceTime > 0) {
            this.selectedDateSubject.next(value);
            return;
        }

        this.ttSelectedDateChange.emit(value);
    }

    private rememberStateChange(): void {
        if (!this.ttRememberId) {
            return;
        }

        this.rememberSubject.next();
    }

    private rememberDateChange(): void {
        if (!this.ttRememberDateId) {
            return;
        }

        this.rememberDateSubject.next(this.toLocalDateString(this.selectedDate));
    }

    private rememberState(): void {
        if (!this.ttRememberId) {
            return;
        }

        const state = this.buildRememberState();

        this.setLocalRememberState(state);
        this.remember.remember(this.ttRememberId, JSON.stringify(state));
    }

    private rememberDate(value: string): void {
        const dateString = this.parseRememberDate(value);

        if (!this.ttRememberDateId || !dateString) {
            return;
        }

        this.setLocalRememberDate(this.ttRememberDateId, dateString);
        this.remember.remember(this.ttRememberDateId, dateString);
    }

    private async loadRememberedState(): Promise<void> {
        if (!this.ttRememberId || this.ttOnlyRemember || this.lastRememberLoadId === this.ttRememberId) {
            return;
        }

        this.lastRememberLoadId = this.ttRememberId;

        const localState = this.getLocalRememberState(this.ttRememberId);

        if (localState) {
            this.applyRememberState(localState, false);
            this.setLocalRememberState(localState);
        }

        try {
            const dbJsonState = (await this.remember.getLastStatus(this.ttRememberId))?.[0]?.variablevalue;
            const dbState = this.parseRememberState(dbJsonState);

            if (dbState && this.isNewerRememberState(dbState, localState)) {
                this.applyRememberState(dbState);
                this.setLocalRememberState(dbState);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async loadRememberedDate(): Promise<void> {
        if (!this.ttRememberDateId || this.ttOnlyRemember || this.lastRememberDateLoadId === this.ttRememberDateId) {
            return;
        }

        this.lastRememberDateLoadId = this.ttRememberDateId;

        const localDate = this.getLocalRememberDate(this.ttRememberDateId);

        if (localDate) {
            this.applyRememberedDate(localDate);
        }

        try {
            const dbDate = this.parseRememberDate((await this.remember.getLastStatus(this.ttRememberDateId))?.[0]?.variablevalue);

            if (dbDate) {
                this.setLocalRememberDate(this.ttRememberDateId, dbDate);
                this.applyRememberedDate(dbDate);
            }
        } catch (error) {
            console.error(error);
        }
    }

    private applyRememberState(state: SchedulerRememberState, emitEvents = true): void {
        const rememberedView = this.normalizeViewName(state.selectedView);

        if (rememberedView && this.viewNames.includes(rememberedView)) {
            this.selectedViewIndex = this.viewNames.indexOf(rememberedView);
            this.selectedViewStateApplied = true;

            if (emitEvents) {
                this.ttSelectedViewChange.emit(this.selectedViewName);
            }
        }

        if (typeof state.showWorkHours === 'boolean') {
            this.showWorkHours = state.showWorkHours;
            this.showWorkHoursStateApplied = true;
        }

        if (emitEvents) {
            this.emitDateRangeChange(this.buildDateRange(this.selectedDate, this.selectedViewName));
        }
    }

    private buildRememberState(): SchedulerRememberState {
        return {
            selectedView: this.selectedViewName,
            showWorkHours: this.showWorkHours,
            updatedAt: new Date().toISOString(),
        };
    }

    private getLocalRememberState(rememberId: string): SchedulerRememberState | null {
        return this.parseRememberState(this.getLocalRememberStates()[rememberId]);
    }

    private setLocalRememberState(state: SchedulerRememberState): void {
        const states = this.getLocalRememberStates();

        states[this.ttRememberId] = this.sanitizeRememberState(state);
        localStorage.setItem(this.schedulerStorageKey, JSON.stringify(states));
    }

    private getLocalRememberStates(): { [key: string]: SchedulerRememberState } {
        const json = localStorage.getItem(this.schedulerStorageKey) ?? '{}';

        try {
            const states = JSON.parse(json);

            return states && typeof states === 'object' && !Array.isArray(states) ? states : {};
        } catch (error) {
            return {};
        }
    }

    private sanitizeRememberState(state: SchedulerRememberState): SchedulerRememberState {
        return {
            selectedView: state.selectedView,
            showWorkHours: state.showWorkHours,
            updatedAt: state.updatedAt ?? new Date().toISOString(),
        };
    }

    private getLocalRememberDate(rememberDateId: string): string | null {
        return this.parseRememberDate(this.getLocalRememberDates()[rememberDateId]);
    }

    private setLocalRememberDate(rememberDateId: string, value: string): void {
        const dateString = this.parseRememberDate(value);

        if (!dateString) {
            return;
        }

        const dates = this.getLocalRememberDates();

        dates[rememberDateId] = dateString;
        localStorage.setItem(this.schedulerDateStorageKey, JSON.stringify(dates));
    }

    private getLocalRememberDates(): { [key: string]: string } {
        const json = localStorage.getItem(this.schedulerDateStorageKey) ?? '{}';

        try {
            const dates = JSON.parse(json);

            return dates && typeof dates === 'object' && !Array.isArray(dates) ? dates : {};
        } catch (error) {
            return {};
        }
    }

    private parseRememberState(value: unknown): SchedulerRememberState | null {
        if (!value) {
            return null;
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            return value as SchedulerRememberState;
        }

        if (typeof value !== 'string') {
            return null;
        }

        try {
            const state = JSON.parse(value);

            return state && typeof state === 'object' && !Array.isArray(state) ? state : null;
        } catch (error) {
            return null;
        }
    }

    private parseRememberDate(value: unknown): string | null {
        const date = this.toDate(value);

        return date ? this.toLocalDateString(date) : null;
    }

    private applyRememberedDate(value: unknown): boolean {
        const dateString = this.parseRememberDate(value);

        if (!dateString) {
            return false;
        }

        const date = this.toDate(dateString);

        if (!date) {
            return false;
        }

        const selectedDate = this.startOfDay(date);
        const didChange = !this.isSameDateTime(selectedDate, this.selectedDate) || this.ttSelectedDate !== dateString;

        this.selectedDate = selectedDate;
        this.selectedDateStateApplied = true;
        this.ttSelectedDate = dateString;

        if (didChange) {
            this.emitSelectedDateChange(dateString);
            this.emitDateRangeChange(this.buildDateRange(this.selectedDate, this.selectedViewName));
        }

        return true;
    }

    private isNewerRememberState(state: SchedulerRememberState, currentState: SchedulerRememberState | null): boolean {
        if (!currentState) {
            return true;
        }

        return this.getRememberStateTime(state) > this.getRememberStateTime(currentState);
    }

    private getRememberStateTime(state: SchedulerRememberState | null): number {
        const value = state?.updatedAt ? Date.parse(state.updatedAt) : 0;

        return Number.isFinite(value) ? value : 0;
    }

    private isSameDateTime(left: Date, right: Date): boolean {
        return left instanceof Date && right instanceof Date && left.getTime() === right.getTime();
    }

    private rebuildSchedulerData(): void {
        const groupFields = this.getGroupFields(this.ttData);
        const items = this.getDataItems(this.ttData);

        this.events = items.map((item) => this.mapItemToEvent(item)).filter((event): event is SchedulerDisplayEvent<TItem> => !!event);
        this.resources = this.buildResources(groupFields);
        this.group = {
            resources: this.getGroupedResourceNames(groupFields),
            orientation: this.ttSetup?.groupOrientation ?? 'vertical',
        };
    }

    private mapItemToEvent(item: TItem): SchedulerDisplayEvent<TItem> | null {
        const start = this.toDate(item['start_dt'] ?? item['start']);

        if (!start) {
            return null;
        }

        const parsedEnd = this.toDate(item['end_dt'] ?? item['end']);
        const end = parsedEnd && parsedEnd.getTime() > start.getTime() ? parsedEnd : new Date(start.getTime() + 30 * 60000);
        const event = {
            ...item,
            id: item['item_id'] ?? item['id'],
            title: `${item['item_name'] ?? item['title'] ?? ''}`,
            description: `${item['description'] ?? ''}`,
            start,
            end,
            isAllDay: this.toBoolean(item['isAllDay'] ?? item['is_all_day'], false),
            dataItem: item,
        } as SchedulerDisplayEvent<TItem>;

        if (item['portal_user_keyno'] !== undefined && event['ownerId'] === undefined) {
            event['ownerId'] = item['portal_user_keyno'];
        }

        this.copyDimensions(item, event);

        return event;
    }

    private copyDimensions(item: TItem, event: SchedulerDisplayEvent<TItem>): void {
        const dims = item['dims'];

        if (!Array.isArray(dims)) {
            return;
        }

        dims.forEach((dim) => {
            if (!dim || typeof dim !== 'object') {
                return;
            }

            Object.keys(dim).forEach((key) => {
                event[key] = Array.isArray(dim[key]) ? [...dim[key]] : dim[key];
            });
        });
    }

    private buildResources(groupFields: string[]): Resource[] {
        const explicitResources = this.getExplicitResources();

        if (explicitResources.length > 0) {
            return [
                {
                    name: 'ownerId',
                    field: 'ownerId',
                    data: explicitResources.map((resource) => this.mapResourceItem(resource)),
                    valueField: 'value',
                    textField: 'text',
                    colorField: 'color',
                    multiple: false,
                },
            ];
        }

        const resourceRows = this.getResourceItems(this.ttData);

        return groupFields
            .map((field) => ({
                name: field,
                field,
                data: resourceRows.filter((resource) => resource['item_dim'] === field),
                valueField: 'item_id',
                textField: 'item_name',
                colorField: 'color',
                multiple: false,
            }))
            .filter((resource) => resource.data.length > 0);
    }

    private getExplicitResources(): SchedulerDataItem[] {
        return this.ttResources?.length > 0 ? this.ttResources : (this.ttOwners ?? []);
    }

    private getGroupedResourceNames(groupFields: string[]): string[] {
        const groupResources = this.ttSetup?.groupResources;

        if (groupResources === true) {
            return this.resources.map((resource) => resource.name ?? resource.field).filter((name): name is string => !!name);
        }

        if (Array.isArray(groupResources)) {
            return groupResources.filter((name) => this.resources.some((resource) => (resource.name ?? resource.field) === name));
        }

        return groupFields.filter((field) => this.resources.some((resource) => (resource.name ?? resource.field) === field));
    }

    private mapResourceItem(resource: SchedulerDataItem): SchedulerDataItem {
        return {
            ...resource,
            value: resource['value'] ?? resource['portal_user_keyno'] ?? resource['item_id'],
            text: resource['text'] ?? resource['portal_user_name'] ?? resource['item_name'] ?? '',
            color: resource['color'] ?? resource['background_color_hex'] ?? resource['color_hex'] ?? resource['item_color'],
        };
    }

    private getEventStyleColor(event: SchedulerDisplayEvent<TItem>, data: TItem): string | number | undefined {
        return this.getStyleValue(data?.['color'] ?? event?.['color'] ?? this.getEventResourceStyleValue(event, 'color'));
    }

    private getEventStyleTextColor(event: SchedulerDisplayEvent<TItem>, data: TItem): string | number | undefined {
        return this.getStyleValue(data?.['color_text'] ?? event?.['color_text'] ?? this.getEventResourceStyleValue(event, 'color_text'));
    }

    private getContrastTextColor(backgroundColor: string | number | undefined): string | undefined {
        if (typeof backgroundColor !== 'string') {
            return undefined;
        }

        const color = this.parseCssColor(backgroundColor);

        if (!color) {
            return undefined;
        }

        const luminance = this.getRelativeLuminance(color);
        const blackContrast = (luminance + 0.05) / 0.05;
        const whiteContrast = 1.05 / (luminance + 0.05);

        return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
    }

    private parseCssColor(value: string): SchedulerRgbColor | null {
        const color = value.trim().toLowerCase();

        if (color.startsWith('#')) {
            return this.parseHexColor(color);
        }

        const rgbMatch = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/);

        if (!rgbMatch) {
            return null;
        }

        return this.normalizeRgbColor(+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]);
    }

    private parseHexColor(value: string): SchedulerRgbColor | null {
        const hex = value.replace('#', '');

        if (hex.length === 3 || hex.length === 4) {
            return this.normalizeRgbColor(parseInt(hex[0] + hex[0], 16), parseInt(hex[1] + hex[1], 16), parseInt(hex[2] + hex[2], 16));
        }

        if (hex.length === 6 || hex.length === 8) {
            return this.normalizeRgbColor(parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16));
        }

        return null;
    }

    private normalizeRgbColor(red: number, green: number, blue: number): SchedulerRgbColor | null {
        if (![red, green, blue].every((value) => Number.isFinite(value) && value >= 0 && value <= 255)) {
            return null;
        }

        return { red, green, blue };
    }

    private getRelativeLuminance(color: SchedulerRgbColor): number {
        const red = this.getLinearColorChannel(color.red);
        const green = this.getLinearColorChannel(color.green);
        const blue = this.getLinearColorChannel(color.blue);

        return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    }

    private getLinearColorChannel(value: number): number {
        const channel = value / 255;

        return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    }

    private getEventResourceStyleValue(event: SchedulerDisplayEvent<TItem>, fallbackField: string): unknown {
        for (const resource of this.resources) {
            const resourceItem = this.findEventResourceItem(event, resource);

            if (!resourceItem) {
                continue;
            }

            const colorField = fallbackField === 'color' ? (resource.colorField ?? fallbackField) : fallbackField;
            const value = resourceItem[colorField];

            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }

        return undefined;
    }

    private findEventResourceItem(event: SchedulerDisplayEvent<TItem>, resource: Resource): SchedulerDataItem | undefined {
        const field = resource.field;
        const data = resource.data as SchedulerDataItem[] | undefined;

        if (!field || !Array.isArray(data)) {
            return undefined;
        }

        const eventValue = event[field] ?? this.getEventDataItem(event)?.[field];
        const eventValues = Array.isArray(eventValue) ? eventValue : [eventValue];
        const valueField = resource.valueField ?? 'value';

        return data.find((item) => eventValues.some((value) => this.isSameResourceValue(item?.[valueField], value)));
    }

    private isSameResourceValue(left: unknown, right: unknown): boolean {
        if (left === right) {
            return true;
        }

        if (left === undefined || left === null || right === undefined || right === null) {
            return false;
        }

        return `${left}` === `${right}`;
    }

    private getStyleValue(value: unknown): string | number | undefined {
        if (typeof value === 'number') {
            return value;
        }

        if (typeof value === 'string' && value.trim() !== '') {
            return value;
        }

        return undefined;
    }

    private getDataItems(data: SchedulerDataInput<TItem>): TItem[] {
        if (Array.isArray(data)) {
            return data;
        }

        const items = data?.items;

        if (Array.isArray(items)) {
            return items;
        }

        const records = data?.records;

        if (Array.isArray(records)) {
            return records;
        }

        return [];
    }

    private getResourceItems(data: SchedulerDataInput<TItem>): SchedulerDataItem[] {
        const resources = this.getObjectArray(data, 'resources');
        const items = this.getObjectArray(data, 'items');

        return [...resources, ...items].filter((item) => item?.['item_dim'] && !this.isTrue(item?.['isgroup']));
    }

    private getGroupFields(data: SchedulerDataInput<TItem>): string[] {
        const groups = this.getObjectArray(data, 'groups');
        const groupFields = groups.filter((item) => this.isTrue(item?.['isgroup']) && item?.['item_dim']).map((item) => `${item['item_dim']}`);

        return Array.from(new Set(groupFields));
    }

    private getObjectArray(data: SchedulerDataInput<TItem>, key: 'groups' | 'items' | 'records' | 'resources'): SchedulerDataItem[] {
        if (Array.isArray(data)) {
            const value = (data as SchedulerDataItem)[key];
            return Array.isArray(value) ? value : [];
        }

        const value = data?.[key];

        return Array.isArray(value) ? value : [];
    }

    private emitItemChange(action: SchedulerItemChangeAction, dataItem: SchedulerDisplayEvent<TItem>, start: Date, end: Date, previousDataItem = dataItem): void {
        const item = this.getEventDataItem(dataItem);

        if (!this.ttEditable || this.isReadOnlyItem(item)) {
            return;
        }

        const previousValue = this.buildItemChangeValue(this.toDate(previousDataItem?.start ?? previousDataItem?.['start_dt'] ?? item['start'] ?? item['start_dt']) ?? start, this.toDate(previousDataItem?.end ?? previousDataItem?.['end_dt'] ?? item['end'] ?? item['end_dt']) ?? end);
        const currentValue = this.buildItemChangeValue(start, end);
        const updatedItem = {
            ...item,
            start,
            end,
            start_dt: this.toLocalDateTimeString(start),
            end_dt: this.toLocalDateTimeString(end),
        } as TItem;
        const updatedEvent = this.applyLocalItemChange(dataItem, item, updatedItem);

        this.ttEventChange.emit({
            action,
            item: updatedItem,
            start,
            startDatestring: currentValue.startDatestring,
            end,
            endDatestring: currentValue.endDatestring,
            previousStart: previousValue.start,
            previousStartDatestring: previousValue.startDatestring,
            previousEnd: previousValue.end,
            previousEndDatestring: previousValue.endDatestring,
            previousValue,
            currentValue,
            schedulerEvent: updatedEvent,
        });
    }

    private buildItemChangeValue(start: Date, end: Date): SchedulerItemChangeValue {
        return {
            start,
            startDatestring: this.toOffsetDateTimeString(start),
            end,
            endDatestring: this.toOffsetDateTimeString(end),
        };
    }

    private buildSlotSelectedEvent(event: SchedulerSlotSelectionSource): SchedulerSlotSelected {
        return {
            start: event.start,
            startDatestring: this.toOffsetDateTimeString(event.start),
            end: event.end,
            endDatestring: this.toOffsetDateTimeString(event.end),
            isAllDay: event.isAllDay ?? false,
            resources: event.resources ?? [],
            originalEvent: event.originalEvent,
        };
    }

    private applyLocalItemChange(event: SchedulerDisplayEvent<TItem>, item: TItem, updatedItem: TItem): SchedulerDisplayEvent<TItem> {
        Object.assign(item, {
            start: updatedItem['start'],
            end: updatedItem['end'],
            start_dt: updatedItem['start_dt'],
            end_dt: updatedItem['end_dt'],
        });

        const updatedEvent = {
            ...event,
            ...updatedItem,
            start: updatedItem['start'],
            end: updatedItem['end'],
            dataItem: item,
        } as SchedulerDisplayEvent<TItem>;
        const updatedId = updatedEvent['item_id'] ?? updatedEvent.id;

        this.events = this.events.map((existingEvent) => (existingEvent === event || this.isSameEventIdentity(existingEvent, updatedId) ? updatedEvent : existingEvent));

        return updatedEvent;
    }

    private isSameEventIdentity(event: SchedulerDisplayEvent<TItem>, id: unknown): boolean {
        if (id === undefined || id === null) {
            return false;
        }

        const eventId = event['item_id'] ?? event.id;

        return eventId === id || `${eventId}` === `${id}`;
    }

    private emitDateRangeChange(dateRange: DateRange): void {
        this.ttDateRangeChange.emit({
            start: dateRange.start,
            end: dateRange.end,
            fom: this.toLocalDateString(dateRange.start),
            tom: this.toLocalDateString(this.getInclusiveEndDate(dateRange.end)),
            view: this.selectedViewName,
            dateRange,
        });
    }

    private getEventDataItem(event: SchedulerDisplayEvent<TItem> | undefined): TItem {
        return (event?.dataItem ?? event ?? {}) as unknown as TItem;
    }

    private navigateEventItem(item: SchedulerDataItem | undefined, originalEvent: Event): void {
        const itemState = this.getNavigationValue(item?.['item_state']);

        if (!itemState) {
            return;
        }

        const itemParms = this.getNavigationValue(item?.['item_parms']);

        if (this.isNavigationModifierPressed(originalEvent)) {
            this.stateService.newTab(itemState, itemParms);
            return;
        }

        this.stateService.go(itemState, itemParms);
    }

    private isNavigationModifierPressed(event: Event): boolean {
        return (event instanceof MouseEvent || event instanceof KeyboardEvent) && this.utility.isModifierKeyPressed(event);
    }

    private getNavigationValue(value: unknown): string | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        const text = `${value}`.trim();

        return text === '' ? undefined : text;
    }

    private isReadOnlyItem(item: SchedulerDataItem | undefined): boolean {
        return item?.['editable'] === false || item?.['editable'] === 'false' || item?.['readonly'] === true || item?.['readonly'] === 'true';
    }

    private getEditableSettings(value: SchedulerSetup['editable']): EditableSettings {
        const defaults: EditableSettings = {
            remove: true,
            drag: true,
            resize: true,
            add: false,
            edit: false,
        };

        if (value === false) {
            return {
                remove: false,
                drag: false,
                resize: false,
                add: false,
                edit: false,
            };
        }

        if (value === true || value === undefined) {
            return defaults;
        }

        return {
            ...defaults,
            ...value,
        };
    }

    private getViewNamesForMode(mode: SchedulerMode): SchedulerViewName[] {
        if (mode === 'timeline') {
            return ['timeline', 'timelineWeek', 'timelineMonth'];
        }

        if (mode === 'both') {
            return ['timeline', 'timelineWeek', 'timelineMonth', 'day', 'workWeek', 'week', 'month', 'agenda'];
        }

        return ['day', 'workWeek', 'week', 'month', 'agenda'];
    }

    private getDefaultViewForMode(mode: SchedulerMode): SchedulerViewName {
        return mode === 'timeline' ? 'timeline' : 'week';
    }

    private normalizeViewName(value: SchedulerSetup['selectedview']): SchedulerViewName | undefined {
        switch (value) {
            case 'timeline':
            case 'timelineWeek':
            case 'timelineMonth':
            case 'day':
            case 'workWeek':
            case 'week':
            case 'month':
            case 'agenda':
                return value;
            case 'timelineWorkWeek':
                return 'timelineWeek';
            case 'timelineQuarter':
            case 'timelineYear':
            case 'timelineWeekYear':
            case 'year':
                return 'timelineMonth';
            default:
                return undefined;
        }
    }

    private buildDateRange(date: Date, view: SchedulerViewName): DateRange {
        let start = this.startOfDay(date);
        let end = new Date(start);

        switch (view) {
            case 'workWeek':
                start = this.startOfWeek(date);
                end = this.addDays(start, 5);
                break;
            case 'week':
            case 'timelineWeek':
            case 'agenda':
                start = this.startOfWeek(date);
                end = this.addDays(start, 7);
                break;
            case 'month':
            case 'timelineMonth':
                start = new Date(date.getFullYear(), date.getMonth(), 1);
                end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
                break;
            case 'day':
            case 'timeline':
            default:
                end = this.addDays(start, 1);
                break;
        }

        return {
            start,
            end,
            text: `${this.toLocalDateString(start)} - ${this.toLocalDateString(this.getInclusiveEndDate(end))}`,
            shortText: `${this.toLocalDateString(start)} - ${this.toLocalDateString(this.getInclusiveEndDate(end))}`,
        };
    }

    private startOfWeek(date: Date): Date {
        const start = this.startOfDay(date);
        const day = start.getDay();
        const diff = (day - Number(this.weekStart) + 7) % 7;

        return this.addDays(start, -diff);
    }

    private startOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private addDays(date: Date, days: number): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
    }

    private getInclusiveEndDate(exclusiveEnd: Date): Date {
        return new Date(exclusiveEnd.getTime() - 1);
    }

    private toDate(value: unknown): Date | null {
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return new Date(value);
        }

        if (typeof value === 'number') {
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? null : date;
        }

        if (typeof value !== 'string' || value.trim() === '') {
            return null;
        }

        const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);

        if (match) {
            const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
            const date = new Date(+year, +month - 1, +day, +hour, +minute, +second);
            return Number.isNaN(date.getTime()) ? null : date;
        }

        const date = new Date(value);

        return Number.isNaN(date.getTime()) ? null : date;
    }

    private toLocalDateTimeString(date: Date): string {
        return `${this.toLocalDateString(date)} ${this.pad(date.getHours())}:${this.pad(date.getMinutes())}:${this.pad(date.getSeconds())}`;
    }

    private toOffsetDateTimeString(date: Date): string {
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().replace('T', ' ');
    }

    private toLocalDateString(date: Date): string {
        return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
    }

    private pad(value: number): string {
        return `${value}`.padStart(2, '0');
    }

    private toNumber(value: unknown, fallback: number): number {
        const numberValue = Number(value);

        return Number.isFinite(numberValue) ? numberValue : fallback;
    }

    private toBoolean(value: unknown, fallback: boolean): boolean {
        if (value === undefined || value === null || value === '') {
            return fallback;
        }

        return this.isTrue(value);
    }

    private isTrue(value: unknown): boolean {
        return value === true || value === 'true' || value === '1' || value === 1;
    }
}

export type SchedulerMode = 'default' | 'timeline' | 'both';

export type SchedulerViewName = 'day' | 'workWeek' | 'week' | 'month' | 'agenda' | 'timeline' | 'timelineWeek' | 'timelineMonth';

export type SchedulerItemChangeAction = 'drag' | 'resize' | 'save';

export interface SchedulerDataItem {
    [key: string]: any;
}

export type SchedulerDataInput<TItem extends SchedulerDataItem = SchedulerDataItem> = TItem[] | SchedulerGroupedData<TItem> | null | undefined;

export interface SchedulerGroupedData<TItem extends SchedulerDataItem = SchedulerDataItem> {
    items?: TItem[];
    records?: TItem[];
    groups?: SchedulerDataItem[];
    resources?: SchedulerDataItem[];
    [key: string]: any;
}

export interface SchedulerSetup {
    date_fom?: string | Date;
    selectedview?: SchedulerViewName | 'timelineWorkWeek' | 'timelineQuarter' | 'timelineYear' | 'timelineWeekYear' | 'year' | string;
    templateStyle?: string;
    editable?: boolean | Partial<EditableSettings>;
    height?: number;
    eventHeight?: number;
    columnWidth?: number;
    showWorkHours?: boolean;
    allDaySlot?: boolean;
    weekStart?: number;
    startTime?: string;
    endTime?: string;
    workDayStart?: string;
    workDayEnd?: string;
    slotDuration?: number;
    slotDivisions?: number;
    scrollTime?: string | Date;
    groupResources?: boolean | string[];
    groupOrientation?: 'horizontal' | 'vertical';
}

export interface SchedulerRememberState {
    selectedView?: SchedulerViewName | string;
    showWorkHours?: boolean;
    updatedAt?: string;
}

export interface SchedulerDisplayEvent<TItem extends SchedulerDataItem = SchedulerDataItem> extends SchedulerEvent {
    dataItem: TItem;
    [key: string]: any;
}

export interface SchedulerItemChange<TItem extends SchedulerDataItem = SchedulerDataItem> {
    action: SchedulerItemChangeAction;
    item: TItem;
    start: Date;
    startDatestring: string;
    end: Date;
    endDatestring: string;
    previousStart: Date;
    previousStartDatestring: string;
    previousEnd: Date;
    previousEndDatestring: string;
    previousValue: SchedulerItemChangeValue;
    currentValue: SchedulerItemChangeValue;
    schedulerEvent: SchedulerDisplayEvent<TItem>;
}

export interface SchedulerItemChangeValue {
    start: Date;
    startDatestring: string;
    end: Date;
    endDatestring: string;
}

export interface SchedulerDateRangeChange {
    start: Date;
    end: Date;
    fom: string;
    tom: string;
    view: SchedulerViewName;
    dateRange: DateRange;
}

export interface SchedulerEventClick<TItem extends SchedulerDataItem = SchedulerDataItem> {
    item: TItem;
    schedulerEvent: SchedulerDisplayEvent<TItem>;
    originalEvent: Event;
}

export interface SchedulerSlotSelected {
    start: Date;
    startDatestring: string;
    end: Date;
    endDatestring: string;
    isAllDay: boolean;
    resources: any[];
    originalEvent?: Event;
}

export interface SchedulerSlotClick extends SchedulerSlotSelected {
    originalEvent: Event;
}

type SchedulerSlotSelectionSource = Pick<SchedulerSlotSelected, 'start' | 'end'> & Partial<Pick<SchedulerSlotSelected, 'isAllDay' | 'resources' | 'originalEvent'>>;

type SchedulerLocale = 'en' | 'zh' | 'pl' | 'it' | 'fr' | 'lt' | 'fi' | 'es' | 'de' | 'da' | 'se' | 'no';

type SchedulerIntlService = IntlService & {
    localeId: string;
};

interface SchedulerRgbColor {
    red: number;
    green: number;
    blue: number;
}

export interface SchedulerMessages {
    allEvents: string;
    allDay: string;
    dateHeader: string;
    timeHeader: string;
    eventHeader: string;
    deleteTitle: string;
    nextTitle: string;
    previousTitle: string;
    today: string;
    calendarToday: string;
    showFullDay: string;
    showWorkDay: string;
    dayViewTitle: string;
    weekViewTitle: string;
    workWeekViewTitle: string;
    monthViewTitle: string;
    timelineViewTitle: string;
    timelineWeekViewTitle: string;
    timelineMonthViewTitle: string;
    agendaViewTitle: string;
    cancel: string;
    save: string;
    destroy: string;
    deleteConfirmation: string;
    deleteDialogTitle: string;
    selectView: string;
}

const SCHEDULER_LOCALES = new Set<SchedulerLocale>(['en', 'zh', 'pl', 'it', 'fr', 'lt', 'fi', 'es', 'de', 'da', 'se', 'no']);

const SCHEDULER_MESSAGE_FALLBACKS: SchedulerMessages = {
    allEvents: 'All events',
    allDay: 'All day',
    dateHeader: 'Date',
    timeHeader: 'Time',
    eventHeader: 'Event',
    deleteTitle: 'Delete',
    nextTitle: 'Next',
    previousTitle: 'Previous',
    today: 'Today',
    calendarToday: 'Today',
    showFullDay: 'Show full day',
    showWorkDay: 'Show business hours',
    dayViewTitle: 'Day',
    weekViewTitle: 'Week',
    workWeekViewTitle: 'Work week',
    monthViewTitle: 'Month',
    timelineViewTitle: 'Timeline',
    timelineWeekViewTitle: 'Timeline week',
    timelineMonthViewTitle: 'Timeline month',
    agendaViewTitle: 'Agenda',
    cancel: 'Cancel',
    save: 'Save',
    destroy: 'Delete',
    deleteConfirmation: 'Are you sure you want to delete this event?',
    deleteDialogTitle: 'Delete event',
    selectView: 'Select view',
};

const SCHEDULER_MESSAGE_WORD_IDS: { [key in keyof SchedulerMessages]: string } = {
    allEvents: 'all_events',
    allDay: 'all_day',
    dateHeader: 'date',
    timeHeader: 'time',
    eventHeader: 'event',
    deleteTitle: 'delete',
    nextTitle: 'next',
    previousTitle: 'previous',
    today: 'today',
    calendarToday: 'today',
    showFullDay: 'show_full_day',
    showWorkDay: 'show_business_hours',
    dayViewTitle: 'day',
    weekViewTitle: 'week',
    workWeekViewTitle: 'work_week',
    monthViewTitle: 'month',
    timelineViewTitle: 'timeline',
    timelineWeekViewTitle: 'timeline_week',
    timelineMonthViewTitle: 'timeline_month',
    agendaViewTitle: 'agenda',
    cancel: 'cancel',
    save: 'save',
    destroy: 'delete',
    deleteConfirmation: 'delete_confirmation',
    deleteDialogTitle: 'delete_event',
    selectView: 'view',
};

function getLocalDateAsString(date: Date): string {
    const timezoneOffset = new Date(date).getTimezoneOffset() * 60000;

    return new Date(new Date(date).getTime() - timezoneOffset).toISOString().substring(0, 10);
}
